import type { ComponentPropsWithoutRef } from 'react'

import { cn } from '@/lib/utils'

const pulse = 'animate-pulse rounded-2xl bg-muted'

export function Skeleton({ className = '', ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn(pulse, className)} {...props} />
}

/** Single or multiple lines of text placeholder */
export function SkeletonText({ lines = 1, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-4', i === lines - 1 && lines > 1 ? 'w-4/5' : 'w-full')} />
      ))}
    </div>
  )
}

export function SkeletonCircle({ className = 'h-10 w-10' }) {
  return <div className={cn(pulse, className)} />
}

/** Stat / card block */
export function SkeletonCard({ className = '' }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]',
        className,
      )}
    >
      <Skeleton className="mb-3 h-3 w-24" />
      <Skeleton className="h-8 w-16" />
    </div>
  )
}

/** Table header + N rows for admin lists */
/** Detail page header + two-column body skeleton */
export function SkeletonDetailLayout() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="space-y-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-64 max-w-full" />
          <Skeleton className="h-4 w-48 max-w-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-2xl border border-border bg-card p-6 lg:col-span-2">
          <Skeleton className="h-5 w-40" />
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
        <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-4 h-5 w-48" />
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-24" />
          ))}
        </div>
      </div>
    </div>
  )
}

/** Stacked form sections placeholder */
export function SkeletonFormSections() {
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-6 h-6 w-48" />
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Skeleton className="mb-6 h-6 w-56" />
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    </div>
  )
}

export function SkeletonTable({ rows = 6, columns = 6 }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="border-b border-border bg-muted/50 px-4 py-3">
        <div className="flex gap-4">
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} className="h-3 flex-1" />
          ))}
        </div>
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex gap-4 px-4 py-3.5">
            {Array.from({ length: columns }, (_, c) => (
              <Skeleton key={c} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Filters / search row above admin data tables */
export function SkeletonListToolbar({ fields = 3 }: { fields?: number }) {
  return (
    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="min-w-[160px] flex-1 space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-10 w-full rounded-2xl" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonPaginationRow() {
  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
      <Skeleton className="h-4 w-44" />
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-8 w-28 rounded-lg" />
        <Skeleton className="h-8 w-20 rounded-lg" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-16 rounded-lg" />
      </div>
    </div>
  )
}

/** Toolbar + table + footer — list pages (products, orders, transactions, etc.) */
export function SkeletonListPage({
  columns,
  rows,
  toolbarFields = 0,
}: {
  columns: number
  rows: number
  toolbarFields?: number
}) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {toolbarFields > 0 ? <SkeletonListToolbar fields={toolbarFields} /> : null}
      <SkeletonTable rows={rows} columns={columns} />
      <SkeletonPaginationRow />
    </div>
  )
}

/** Simple dl-style grid for order / transaction detail */
export function SkeletonDetailFields({ fields = 6 }: { fields?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-5 w-full max-w-xs" />
        </div>
      ))}
    </div>
  )
}

/** Product detail primary card while the product record is fetching */
export function SkeletonProductDetail() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading product">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64 max-w-full" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-lg" />
          <Skeleton className="h-9 w-14 rounded-lg" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        <Skeleton className="h-9 w-28 rounded-full" />
        <Skeleton className="h-9 w-40 rounded-full" />
        <Skeleton className="h-9 w-36 rounded-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-52 w-full max-w-lg rounded-xl" />
    </div>
  )
}

export function SkeletonReviewPanel() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-border bg-card p-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-9 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-9 w-24 rounded-lg" />
      </div>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex justify-between gap-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-3 w-40" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonMetricsPanel() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading metrics">
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-9 w-20" />
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-border bg-muted/30 p-4">
        <Skeleton className="h-3 w-36" />
        <div className="mt-6 flex h-32 items-end gap-1">
          {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
            <Skeleton key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  )
}

/** POS Make Sales — catalog grid + side cart (lg+) */
export function SkeletonPosMakeSales({
  productRows = 8,
  variant = 'full',
}: {
  productRows?: number
  /** `products` — product grid only (catalog refresh) */
  variant?: 'full' | 'products'
}) {
  const productGrid = (
    <ul
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4"
      aria-busy="true"
      aria-label="Loading products"
    >
      {Array.from({ length: productRows }, (_, i) => (
        <li key={i} className="overflow-hidden rounded-2xl border border-border">
          <Skeleton className="aspect-square w-full rounded-none" />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-16" />
          </div>
        </li>
      ))}
    </ul>
  )

  if (variant === 'products') {
    return productGrid
  }

  return (
    <div className="flex h-full min-h-0 w-full gap-4" aria-busy="true" aria-label="Loading POS catalog">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="space-y-2">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-52 max-w-full" />
          </div>
          <Skeleton className="h-10 w-24 rounded-lg" />
        </div>
        <Skeleton className="mb-3 h-12 w-full rounded-2xl" />
        <div className="mb-3 flex gap-2 overflow-hidden">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-24 shrink-0 rounded-full" />
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">{productGrid}</div>
      </div>
      <aside className="border-border bg-card hidden w-full max-w-md shrink-0 flex-col rounded-2xl border p-4 lg:flex">
        <div className="mb-4 flex items-center justify-between">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-8 w-20" />
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-hidden">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
          ))}
        </div>
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full rounded-2xl" />
        </div>
      </aside>
    </div>
  )
}

/** POS Sales History — date filters + sales table */
export function SkeletonPosSalesHistory() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading sales history">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
        <div className="mb-6 space-y-2">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
        <SkeletonListToolbar fields={3} />
        <SkeletonTable rows={8} columns={6} />
        <SkeletonPaginationRow />
      </div>
    </div>
  )
}

/** Commerce hub — wallet tiles, activity table, gateways, pickup & withdrawals */
export function SkeletonCommerceHub() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading payments and wallet">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
        <div className="mb-6 space-y-2">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-4 w-full max-w-xl" />
        </div>
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Skeleton className="h-10 w-56 rounded-lg" />
          <Skeleton className="h-10 w-24 rounded-lg" />
        </div>
        <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4">
              <Skeleton className="mb-2 h-3 w-20" />
              <Skeleton className="h-7 w-28" />
            </div>
          ))}
        </div>
        <div className="mb-8 space-y-3">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-3 w-full max-w-lg" />
          <SkeletonTable rows={5} columns={6} />
        </div>
        <div className="mb-8 space-y-4 rounded-xl border border-border bg-muted/15 p-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-full max-w-md" />
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex items-center justify-between gap-4 py-1">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
          <Skeleton className="h-10 w-44 rounded-lg" />
        </div>
        <Skeleton className="mb-3 h-3 w-36" />
        <div className="mb-8 space-y-2">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="mb-4 h-3 w-32" />
        <SkeletonTable rows={3} columns={5} />
        <Skeleton className="mb-4 mt-8 h-3 w-40" />
        <SkeletonTable rows={4} columns={4} />
      </div>
    </div>
  )
}
